import { User, Context, Branch } from '@/types';

export interface Organisation {
  id: number;
  name: string;
  additional_contact_info?: string;
  organisation_parent_id?: number;
  branch_id?: number;
  general_reserve?: number;
  logo?: string;
  use_image?: boolean;
  use_time?: boolean;
  use_environment?: boolean;
  use_safety?: boolean;
  use_objective?: boolean;

  parent?: Organisation | null;
  users?: User[];
  scales?: OrganisationScale;
  contexts?: Context[];
  general_reserve_items?: GeneralReserveItem[];
  branch?: Branch;
}

export interface GeneralReserveItem {
  id: number;
  organisation_id: number;
  name: string;
  description?: string;
  amount: number;

  organisation?: Organisation;
}

export const GENERAL_RESERVE_ITEM_EDITABLE_FIELDS = [
  "organisation_id",
  "name",
  "description",
  "amount",
] as const;

export const ORGANISATION_EDITABLE_FIELDS = [
  "id",
  "name",
  "additional_contact_info",
  "organisation_parent_id",
  "logo",
  "use_image",
  "use_time",
  "use_environment",
  "use_safety",
  "use_objective",
  "branch_id",
] as const;

export interface FormattedGeneralReserveItem extends GeneralReserveItem {
  amount_formatted?: string;
}

export interface FormattedOrganisation extends Organisation {
  general_reserve_formatted?: string | null;
  general_reserve_items?: FormattedGeneralReserveItem[];
}

export interface OrganisationScale {
  id: number;
  organisation_id: number;

  n_a_scale_description_probability: string;
  n_a_scale_description_financial: string;
  n_a_scale_description_image: string;
  n_a_scale_description_time: string;
  n_a_scale_description_environment: string;
  n_a_scale_description_safety: string;
  n_a_scale_description_objective: string;
  n_a_scale_probability_min: null;
  n_a_scale_financial_min: null;

  very_small_scale_description_probability: string;
  very_small_scale_description_financial: string;
  very_small_scale_description_image: string;
  very_small_scale_description_time: string;
  very_small_scale_description_environment: string;
  very_small_scale_description_safety: string;
  very_small_scale_description_objective: string;
  very_small_scale_probability_min: 0;
  very_small_scale_financial_min: 0;

  small_scale_description_probability: string;
  small_scale_description_financial: string;
  small_scale_description_image: string;
  small_scale_description_time: string;
  small_scale_description_environment: string;
  small_scale_description_safety: string;
  small_scale_description_objective: string;
  small_scale_probability_min: number;
  small_scale_financial_min: number;

  medium_scale_description_probability: string;
  medium_scale_description_financial: string;
  medium_scale_description_image: string;
  medium_scale_description_time: string;
  medium_scale_description_environment: string;
  medium_scale_description_safety: string;
  medium_scale_description_objective: string;
  medium_scale_probability_min: number;
  medium_scale_financial_min: number;

  large_scale_description_probability: string;
  large_scale_description_financial: string;
  large_scale_description_image: string;
  large_scale_description_time: string;
  large_scale_description_environment: string;
  large_scale_description_safety: string;
  large_scale_description_objective: string;
  large_scale_probability_min: number;
  large_scale_financial_min: number;

  very_large_scale_description_probability: string;
  very_large_scale_description_financial: string;
  very_large_scale_description_image: string;
  very_large_scale_description_time: string;
  very_large_scale_description_environment: string;
  very_large_scale_description_safety: string;
  very_large_scale_description_objective: string;
  very_large_scale_probability_min: number;
  very_large_scale_financial_min: number;
}

export const SCALE_KEYS = [
  "n/a",
  "very_small",
  "small",
  "medium",
  "large",
  "very_large",
] as const;

export type ScaleKey = (typeof SCALE_KEYS)[number];

/**
 * Front-end mapped scale with numeric min values for rendering and input.
 */
export interface MappedOrganisationScale {
  readonly scale: ScaleKey;
  readonly description_probability: string;
  readonly description_financial: string;
  readonly description_image: string;
  readonly description_time: string;
  readonly description_environment: string;
  readonly description_safety: string;
  readonly description_objective: string;
  readonly probability_min: number;
  readonly financial_min: number;
}
